import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Loader2, Trophy, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => (v == null ? '—' : `A$${Math.round(v).toLocaleString()}`);
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

/** Margin per sales rep across their open deals — lowest first so coaching lands where it lifts margin most. */
export function RepMarginLeaderboardCard({ onPickRep, onOpenDeal, filterParams = {}, filterKey = '' }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/by-rep`, { params: filterParams, withCredentials: true })
      .then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || 'Could not load rep margins'));
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const tone = (v) => (v == null ? 'text-slate-400' : v < 0 || (data?.min_margin_pct != null && v < data.min_margin_pct) ? 'text-rose-600' : v < 20 ? 'text-amber-600' : 'text-emerald-700');
  const maxSell = Math.max(1, ...(data?.reps || []).map((r) => r.sell_aud));

  return (
    <Card className="border-slate-200" data-testid="rep-margin-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div>
            <h3 className="text-sm font-semibold text-[#0F2040] flex items-center gap-1.5"><Trophy className="w-4 h-4 text-slate-400" /> Margin by sales rep{data?.filtered && <span className="ml-1 rounded-full bg-cyan-50 border border-[#4ABFCF] px-1.5 py-0.5 text-[10px] font-medium text-[#0F2040]" data-testid="rep-filtered-badge">page filters applied</span>}</h3>
            <p className="text-[11px] text-slate-400">Open deals only (A$), lowest margin first. Click a rep to filter the estimate table below.</p>
          </div>
          {data?.portfolio_margin_pct != null && (
            <span className="text-[10px] text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid="rep-margin-portfolio">
              Portfolio {pct(data.portfolio_margin_pct)} · {data.open_deals} open deals{data.min_margin_pct != null ? ` · guardrail ${pct(data.min_margin_pct)}` : ''}{data.product_sell_aud > 0 ? ` · incl. product ${fmtA(data.product_sell_aud)} → combined ${pct(data.total_margin_pct)}` : ''}
            </span>
          )}
        </div>
        {error && <p className="text-xs text-rose-600" data-testid="rep-margin-error">{error}</p>}
        {!data && !error && <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
        {data && !data.configured && <p className="text-xs text-amber-700" data-testid="rep-margin-not-configured">Set internal cost rates in Admin → Settings first.</p>}
        {data?.configured && data.reps.length === 0 && <p className="text-xs text-slate-400" data-testid="rep-margin-empty">No open deals with pricing yet.</p>}
        {data?.configured && data.reps.length > 0 && (
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs" data-testid="rep-margin-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="py-1.5 pr-2">Rep</th>
                  <th className="py-1.5 pr-2 text-right">Deals</th>
                  <th className="py-1.5 pr-2">Sell (A$)</th>
                  <th className="py-1.5 pr-2 text-right">Cost (A$)</th>
                  <th className="py-1.5 pr-2 text-right">Margin (A$)</th>
                  <th className="py-1.5 pr-2 text-right">Margin %</th>
                  <th className="py-1.5 pr-2 text-right">vs portfolio</th>
                  <th className="py-1.5 pr-2 text-right">Product (A$)</th>
                  <th className="py-1.5 pr-2 text-right">Combined %</th>
                  <th className="py-1.5">Lowest deal</th>
                </tr>
              </thead>
              <tbody>
                {data.reps.map((r) => (
                  <tr key={r.rep_id || 'unassigned'} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onPickRep?.(r.rep_name)} title="Filter the estimate table to this rep" data-testid={`rep-margin-row-${r.rep_id || 'unassigned'}`}>
                    <td className="py-1.5 pr-2 whitespace-nowrap">
                      <span className={`font-semibold ${r.rep_name === 'Unassigned' ? 'text-slate-400' : 'text-[#0F2040]'}`}>{r.rep_name}</span>
                      {r.below > 0 && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-rose-600" title={`${r.below} deal(s) below the margin guardrail`} data-testid={`rep-margin-below-${r.rep_id || 'unassigned'}`}><AlertTriangle className="w-3 h-3" />{r.below}</span>}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{r.deals}</td>
                    <td className="py-1.5 pr-2 min-w-[140px]">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 rounded-full bg-[#4ABFCF]/70" style={{ width: `${Math.max(3, (r.sell_aud / maxSell) * 100)}%` }} />
                        <span className="tabular-nums text-slate-600 whitespace-nowrap">{fmtA(r.sell_aud)}</span>
                      </div>
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">{fmtA(r.cost_aud)}</td>
                    <td className={`py-1.5 pr-2 text-right tabular-nums font-semibold ${r.margin_aud < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmtA(r.margin_aud)}</td>
                    <td className={`py-1.5 pr-2 text-right tabular-nums font-semibold ${tone(r.margin_pct)}`} data-testid={`rep-margin-pct-${r.rep_id || 'unassigned'}`}>{pct(r.margin_pct)}</td>
                    <td className={`py-1.5 pr-2 text-right tabular-nums text-[11px] ${r.vs_portfolio_pts == null || Math.abs(r.vs_portfolio_pts) < 0.05 ? 'text-slate-400' : r.vs_portfolio_pts > 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {r.vs_portfolio_pts == null ? '—' : `${r.vs_portfolio_pts > 0 ? '+' : ''}${r.vs_portfolio_pts} pts`}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600" data-testid={`rep-product-sell-${r.rep_id || 'unassigned'}`}>{r.product_deals ? <span title={`${r.product_deals} deal(s) with a subscription · product margin ${pct(r.product_margin_pct)}`}>{fmtA(r.product_sell_aud)}</span> : <span className="text-slate-300">—</span>}</td>
                    <td className={`py-1.5 pr-2 text-right tabular-nums font-semibold ${r.product_deals ? tone(r.total_margin_pct) : 'text-slate-300'}`} data-testid={`rep-combined-pct-${r.rep_id || 'unassigned'}`}>{r.product_deals ? pct(r.total_margin_pct) : '—'}</td>
                    <td className="py-1.5 text-[11px] max-w-[220px] truncate">
                      {r.lowest ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); onOpenDeal?.(r.lowest.id); }} className="text-[#0F2040] hover:underline truncate" data-testid={`rep-margin-lowest-${r.rep_id || 'unassigned'}`}>
                          {r.lowest.name} <span className={tone(r.lowest.margin_pct)}>({pct(r.lowest.margin_pct)})</span>
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
